import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router";
import { formatRelative } from "../../shared/i18n/index.ts";
import type { NotificationItem } from "../../shared/types.ts";
import { EmptyState } from "../components/EmptyState.tsx";
import { api } from "../lib/api.ts";
import { serverNow } from "../lib/clock.ts";
import { useI18n } from "../lib/i18n.tsx";
import { useMe } from "../lib/me.tsx";

const icons: Record<string, string> = {
  purchase_received: "🛍",
  contact_shared: "☎",
  alert_reached: "🔔",
  watch_ending_soon: "⏳",
  watch_sold_out: "✓",
  watch_floor_lowered: "↓",
  drop_reminder: "◔",
  drop_start: "◉",
  done_requested: "✓",
  purchase_completed: "✓",
  purchase_cancelled: "✕",
  problem_filed: "!",
  problem_replied: "!",
  problem_resolved: "!",
  strike_issued: "!",
  account_suspended: "!",
  invite_requested: "✉",
  invite_granted: "✉",
};

export function Notifications() {
  const { t, locale } = useI18n();
  const { me, loading } = useMe();
  const [items, setItems] = useState<NotificationItem[] | null>(null);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!me) return;
    let live = true;
    api<{ items: NotificationItem[]; unread: number }>("/api/me/notifications")
      .then((r) => {
        if (!live) return;
        setItems(r.items);
        setUnread(r.unread);
      })
      .catch(() => live && setItems([]));
    return () => {
      live = false;
    };
  }, [me]);

  if (loading) return <div className="page muted">{t("common.loading")}</div>;
  if (!me) return <Navigate to="/login?next=/me/notifications" replace />;

  async function markAll() {
    const r = await api<{ unread: number }>("/api/me/notifications/read", { body: { all: true } });
    setUnread(r.unread);
    setItems((list) => list?.map((n) => ({ ...n, read_at: n.read_at ?? serverNow() })) ?? null);
  }

  async function open(n: NotificationItem) {
    if (n.read_at === null) {
      void api("/api/me/notifications/read", { body: { ids: [n.id] } }).catch(() => undefined);
      setItems(
        (list) => list?.map((x) => (x.id === n.id ? { ...x, read_at: serverNow() } : x)) ?? null,
      );
      setUnread((u) => Math.max(0, u - 1));
    }
  }

  const now = serverNow();
  return (
    <div className="page page--narrow">
      <div className="row row--between">
        <h1 className="h1">{t("notif.title")}</h1>
        {unread > 0 ? (
          <button className="btn btn--ghost" onClick={() => void markAll()} data-testid="mark-all">
            {t("notif.mark_all")}
          </button>
        ) : null}
      </div>
      {items === null ? (
        <p className="muted">{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <EmptyState text={t("empty.notifications")} cta={{ label: t("nav.browse"), to: "/s" }} />
      ) : (
        <ul className="list" data-testid="notifications">
          {items.map((n) => (
            <li key={n.id} className={`notif${n.read_at === null ? " notif--unread" : ""}`}>
              <Link
                to={n.url ?? "/me/notifications"}
                className="notif__link"
                onClick={() => void open(n)}
              >
                <span className="notif__icon" aria-hidden="true">
                  {icons[n.type] ?? "•"}
                </span>
                <span className="notif__text">
                  <span className="notif__title">{n.title}</span>
                  {n.body ? <span className="notif__body">{n.body}</span> : null}
                  <span className="hint">{formatRelative(n.created_at, now, locale)}</span>
                </span>
                {n.read_at === null ? <span className="notif__dot" aria-hidden="true" /> : null}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
